import { Box } from '@mui/material';
import { DataGridProProps, GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ManagerReport } from '../../../@types/api';
import { appendTotalsRow, getTotalsRowClass } from '../../../helpers';
import { createNumberColumn } from '../../../helpers/columns';
import { defaultColumnsManagersReports } from '../../../helpers/table';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { createColumn } from '../../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../../molecules/DateRangeSelect';
import TableTemplate from '../../templates/TableTemplate';
import { WALLET_TYPE } from '../../../constants/enums';
import API_ROUTES from '../../../constants/apiRoutes';
import { partnerReportsStyles } from './partnersReportsTableConfig';

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Total',
    description: 'Total',
    headerClassName: 'customGroupHeader',
    children: [
      { field: 'totalStake' },
      { field: 'totalWinnings' },
      { field: 'jackpotAmount' },
      { field: 'totalProfit' },
      { field: 'totalCommission' },
      { field: 'totalProfitAfterCommission' },
    ],
  },
  {
    groupId: 'Tickets',
    description: 'Tickets',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'ticketsTotal' }, { field: 'ticketsLost' }, { field: 'ticketsWon' }, { field: 'ticketsOpen' }],
  },
];

const columns: GridColDef[] = [
  createColumn('username', 'Username', { minWidth: 150, alwaysVisible: true }),
  createColumn('fullName', 'Name', { minWidth: 150 }),
  createColumn('partnersCount', 'Partners'),
  createColumn('playersCount', 'Players'),
  createNumberColumn('balance', 'Balance'),
  createNumberColumn('totalStake', 'Stake'),
  createNumberColumn('totalWinnings', 'Winnings'),
  createNumberColumn('jackpotAmount', 'Jackpots'),
  createNumberColumn('totalProfit', 'Profit'),
  createNumberColumn('totalCommission', 'Commision'),
  createNumberColumn('totalProfitAfterCommission', 'Profit After Comm.'),
  createColumn('ticketsTotal', 'Total'),
  createColumn('ticketsLost', 'Lost'),
  createColumn('ticketsWon', 'Won'),
  createColumn('ticketsOpen', 'Open'),
];

const generateTotalsRow = (data?: ManagerReport) => {
  if (data) {
    return {
      ...data,
      userId: 'totals',
      fullName: 'Totals',
      id: 'totals',
      isTotalsRow: true,
    };
  }
};

const getMobileCardHeaderItems = (report: ManagerReport) => [
  { value: report.fullName, flex: 1, align: 'left' as const },
  { label: 'Profit', value: report.totalProfit.toFixed(2) },
  { label: 'After Comm.', value: report.totalProfitAfterCommission.toFixed(2) },
];

const ManagersReportPage = () => {
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<ManagerReport>(
    API_ROUTES.REPORTS.PARTNERS.MANAGERS,
    {
      page: 1,
      limit: 25,
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
      walletType: WALLET_TYPE.MAIN,
    }
  );

  const totalsRow = generateTotalsRow(data?.totals);

  const aggregatedData = appendTotalsRow(data?.items, totalsRow);

  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  return (
    <Box sx={partnerReportsStyles.container}>
      <TableTemplate
        getRowId={(params) => params.id}
        columnGroupingModel={columnGroupingModel}
        rows={aggregatedData || []}
        columns={columns}
        sx={partnerReportsStyles.table as DataGridProProps['sx']}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        getRowClassName={getTotalsRowClass}
        changeQuery={changeQuery}
        defaultVisibleColumns={defaultColumnsManagersReports}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
        showExport
      />
    </Box>
  );
};

export default ManagersReportPage;
